/**
 * BulkActionBar — canonical primitive for floating bulk-action pills.
 *
 * Owns the animated pill chrome (layout-spring `motion.div`) and a polite
 * live region; callers own the inner content and compose from the exported
 * toolkit: `FadeIn`, `CountedText`, `BulkActionButton`, `SelectAllMatchingToggle`,
 * `DismissProgressBar` (+ the `*_SPRING`/`*_FADE` timing constants).
 */

// React
import {
  createContext, useContext, useEffect, useId, useImperativeHandle,
  useLayoutEffect, useRef, useState, type ReactNode, type Ref,
} from 'react';

// Libraries
import { motion } from 'framer-motion';
import { LoadingSpinnerIcon } from '@/react/components/icons/LoadingSpinnerIcon';

// UI primitives
import { AnimatedCount } from '@/components/ui/AnimatedCount';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CHECKBOX_CLASSES } from '@components/DataTable/constants';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Hooks and utilities
import { cn } from '@/lib/utils';

const COUNT_TOKEN = '%{count}';
const MOUNT_SLIDE_PX = 6;
const NBSP = ' ';
const DismissPausedContext = createContext(false);

const QUEUED_DISMISS_MS = 4000;

export const SHAPE_SPRING = { type: 'spring' as const, bounce: 0.2, visualDuration: 0.35 };
export const MOUNT_SPRING = { type: 'spring' as const, bounce: 0.25, visualDuration: 0.35 };
export const CONTENT_FADE = { duration: 0.14, ease: [0, 0, 0.2, 1] as [number, number, number, number] };
export const MOUNT_FADE = {
  duration: 0.2,
  ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
};

export interface BulkActionBarProps {
  /** Read out by screen readers when the bar mounts or the content swaps. */
  ariaLabel: string;
  /** Inner content — typically a `<FadeIn keyId={mode}>...</FadeIn>` plus any
   *  absolute-positioned overlays (e.g. a dismiss progress bar). */
  children: ReactNode;
  /** Extra classes merged onto the outer pill; positioning belongs to the
   *  parent (the bar just renders the pill). */
  className?: string;
  /** Forwarded to the pill root so parents can anchor/measure it (React 19
   *  ref-as-prop). The bar owns its own motion props, so it intentionally
   *  does not spread arbitrary `...rest` — pass layout classes via `className`. */
  ref?: Ref<HTMLDivElement>;
}

export function BulkActionBar({ ariaLabel, children, className, ref }: BulkActionBarProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  useImperativeHandle(ref, () => rootRef.current!, []);

  useLayoutEffect(() => {
    setHovered(rootRef.current?.matches(':hover') ?? false);
  }, []);

  useLayoutEffect(() => {
    setFocusWithin(rootRef.current?.contains(document.activeElement) ?? false);
  }, [children]);

  return (
    <DismissPausedContext value={hovered || focusWithin}>
      <motion.div
        ref={rootRef}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocusCapture={() => setFocusWithin(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setFocusWithin(false);
        }}
        layout
        initial={{ opacity: 0, y: MOUNT_SLIDE_PX }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: MOUNT_SLIDE_PX }}
        transition={{ layout: SHAPE_SPRING, opacity: MOUNT_FADE, y: MOUNT_SPRING }}
        role="region"
        aria-live="polite"
        aria-label={ariaLabel}
        className={cn(
          'group pointer-events-auto relative flex items-center overflow-hidden rounded-input border-[0.5px] border-dinnerplate-white bg-off-white px-6 py-4 font-inter shadow-soft',
          className,
        )}
      >
        {children}
      </motion.div>
    </DismissPausedContext>
  );
}

export function FadeIn({ keyId, children }: { keyId: string; children: ReactNode }) {
  return (
    <motion.div
      key={keyId}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={CONTENT_FADE}
      className="flex items-center"
    >
      {children}
    </motion.div>
  );
}

export function splitAroundCount(template: string): [string, string] {
  const idx = template.indexOf(COUNT_TOKEN);
  if (idx === -1) return [template, ''];
  return [template.slice(0, idx), template.slice(idx + COUNT_TOKEN.length)];
}

// Edge whitespace on inline-flex items is dropped as "insignificant" in the
// CSS text algorithm, which collapses the word space between the count and
// surrounding copy. Swap leading/trailing runs to non-breaking spaces so
// the gap renders literally.
export function preserveEdgeWhitespace(text: string): string {
  return text
    .replace(/^\s+/, (m) => NBSP.repeat(m.length))
    .replace(/\s+$/, (m) => NBSP.repeat(m.length));
}

// Queued copy in BulkPublishBar ends with an underlined verb ("queued"). Peel
// off the trailing word so it can be styled or turned into a link without
// needing a second i18n key.
export function splitTrailingWord(text: string): [string, string] {
  const trimmed = text.replace(/\s+$/, '');
  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace === -1) return ['', trimmed];
  return [trimmed.slice(0, lastSpace + 1), trimmed.slice(lastSpace + 1)];
}

export interface CountedTextProps {
  count: number;
  template: string;
  className?: string;
}

export function CountedText({ count, template, className }: CountedTextProps) {
  const [pre, post] = splitAroundCount(template);
  return (
    <span className={cn('inline-flex items-center whitespace-nowrap text-[15px] font-semibold leading-[1.5] text-off-black', className)}>
      {pre && <span>{preserveEdgeWhitespace(pre)}</span>}
      <AnimatedCount value={count} />
      {post && <span>{preserveEdgeWhitespace(post)}</span>}
    </span>
  );
}

export interface BulkActionButtonProps {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Tooltip content (dark variant) shown on hover when disabled. */
  disabledReason?: ReactNode;
  className?: string;
}

export function BulkActionButton({ children, onClick, disabled, disabledReason, className }: BulkActionButtonProps) {
  const button = (
    <Button
      type="button"
      variant="tertiary"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'h-auto rounded-xs border border-dinnerplate-white bg-off-white px-4 py-2 text-[15px] font-semibold leading-[1.5] text-off-black hover:bg-dinnerplate-white-lighter',
        'disabled:cursor-not-allowed disabled:text-grey-light disabled:hover:bg-off-white disabled:hover:border-dinnerplate-white',
        className,
      )}
    >
      {children}
    </Button>
  );

  const showTooltip = Boolean(disabled && disabledReason);
  if (!showTooltip) {
    return button;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{button}</span>
        </TooltipTrigger>
        <TooltipContent variant="dark" side="top">{disabledReason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export interface SelectAllMatchingToggleProps {
  checked: boolean;
  disabled?: boolean;
  loading: boolean;
  onToggle: () => void;
  label: string;
  disabledReason?: string;
  className?: string;
}

export function SelectAllMatchingToggle({
  checked,
  disabled = false,
  loading,
  onToggle,
  label,
  disabledReason,
  className,
}: SelectAllMatchingToggleProps) {
  const checkboxId = useId();
  const showTooltip = disabled && !!disabledReason;

  const checkboxNode = loading ? (
    <LoadingSpinnerIcon className="size-6 shrink-0 text-off-black/60" />
  ) : (
    <Checkbox
      id={checkboxId}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onToggle}
      className={cn(CHECKBOX_CLASSES, disabled && 'pointer-events-none')}
    />
  );

  return (
    <label
      htmlFor={loading || disabled ? undefined : checkboxId}
      className={cn(
        'flex cursor-pointer items-center gap-4 whitespace-nowrap rounded-xs border bg-off-white px-4 py-2 text-[15px] leading-[1.5] hover:bg-dinnerplate-white-light',
        checked ? 'border-off-black text-off-black' : 'border-dinnerplate-white text-grey',
        disabled && 'cursor-not-allowed text-grey-light hover:bg-off-white',
        loading && 'cursor-wait hover:bg-off-white',
        className,
      )}
    >
      {showTooltip ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-4">
                {checkboxNode}
                {label}
              </span>
            </TooltipTrigger>
            <TooltipContent
              variant="dark"
              side="top"
              align="start"
              sideOffset={24}
              alignOffset={6}
              arrowPadding={20}
              className="max-w-none whitespace-nowrap"
            >
              {disabledReason}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <>
          {checkboxNode}
          {label}
        </>
      )}
    </label>
  );
}

export interface DismissProgressBarProps {
  /** Unpaused time before dismissal. Defaults to four seconds. */
  durationMs?: number;
  /** Called after durationMs of unpaused time. */
  onComplete?: () => void;
}

/**
 * An absolutely-positioned progress bar that fills across the bottom of the
 * parent pill over `durationMs` ms. Hover or focus within the shell pauses both
 * progress and dismissal. Reduced motion retains a static line and the same timer.
 */
export function DismissProgressBar({ durationMs = QUEUED_DISMISS_MS, onComplete }: DismissProgressBarProps) {
  const paused = useContext(DismissPausedContext);
  const remainingMs = useRef(durationMs);
  const completed = useRef(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    remainingMs.current = durationMs;
    completed.current = false;
  }, [durationMs]);

  useEffect(() => {
    if (paused || completed.current) return;
    const startedAt = performance.now();
    const timer = setTimeout(() => {
      remainingMs.current = 0;
      completed.current = true;
      onCompleteRef.current?.();
    }, remainingMs.current);

    return () => {
      clearTimeout(timer);
      remainingMs.current = Math.max(0, remainingMs.current - (performance.now() - startedAt));
    };
  }, [durationMs, paused]);

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] overflow-hidden">
      <div
        className="h-full origin-left bg-yield-positive animate-[progress-fill_linear_forwards] motion-reduce:animate-none"
        style={{ animationDuration: `${durationMs}ms`, animationPlayState: paused ? 'paused' : 'running' }}
      />
    </div>
  );
}
